import { z } from "zod";
import { ApiInstance, ResponseBaseType } from "../axios-instance";
import { isRoute, Route, RouteTree } from "./route";

type ProxiedRouteCallFunction<T extends Route> = T["requestZodSchema"] extends z.ZodTypeAny
  ? (data: z.input<T["requestZodSchema"]>) => ResponseBaseType<z.infer<T["responseZodSchema"]>>
  : () => ResponseBaseType<z.infer<T["responseZodSchema"]>>;

type ProxiedRoute<T extends Route> = {
  call: ProxiedRouteCallFunction<T> & { fullPath: string };
};

type DeepProxyify<T> = {
  [K in keyof T]: T[K] extends Route ? ProxiedRoute<T[K]> : T[K] extends object ? DeepProxyify<T[K]> : T[K];
};

// Helper function to build the full path by traversing the object path
function getFullPath(routes: RouteTree, routePath: string[]): string {
  let fullPath = "";
  let current = routes;

  // Walk through the object following the path
  for (const segment of routePath) {
    if (segment in current) {
      const value = current[segment];
      if (isRoute(value)) {
        fullPath += "/" + segment;
      } else {
        fullPath += "/" + segment;
        current = value;
      }
    }
  }

  return fullPath;
}

export function createApiProxy<T extends RouteTree>(routes: T): DeepProxyify<T> {
  function createHandler(basePath: string[] = []) {
    return {
      get(target: any, prop: string | symbol) {
        if (typeof prop !== "string") return target[prop];

        const currentPath = [...basePath, prop];
        const value = target[prop];

        if (isRoute(value)) {
          const fullPath = getFullPath(routes, currentPath);

          const call = (data?: any) => {
            if (value.paramsType === "query") {
              return ApiInstance.request({
                method: value.method,
                url: fullPath,
                params: data,
              });
            } else {
              return ApiInstance.request({
                method: value.method,
                url: fullPath,
                data,
              });
            }
          };

          call.fullPath = fullPath;

          return {
            call,
            // getPath: () => fullPath,
            // getInfo: () => ({
            //   ...value,
            //   fullPath,
            // }),
          };
        }

        return new Proxy(value, createHandler(currentPath));
      },
    };
  }

  return new Proxy(routes, createHandler()) as DeepProxyify<T>;
}
